// src/data/legal.ts
// Default copy for all legal / compliance / trust pages, plus the helpers that
// render CMS-editable body text. Written for a New Zealand-based custom bike
// business — wording must never remove customer rights under NZ consumer law
// (the Consumer Guarantees Act 1993 and the Fair Trading Act 1986).
//
// The live copy is editable in the CMS (Site content → Legal pages). These
// values are only the defaults that seed the editor.

// ─── Editable shape (what the CMS stores and the page renders) ──────────────

/** One heading + a block of body text. Body is plain text: blank lines split
 *  paragraphs, and lines beginning with "- " render as bullet points. */
export interface LegalSection {
  heading: string
  body: string
}

export interface LegalDoc {
  /** URL slug — /legal/:slug */
  slug: string
  /** Small mono eyebrow above the title. */
  eyebrow: string
  /** Page H1. */
  title: string
  /** Short lead paragraph under the title. */
  intro: string
  sections: LegalSection[]
}

// Central so trading details read consistently across every document.
export const LEGAL_BUSINESS = {
  tradingName: "Burton Bikes",
  location: "Cambridge, New Zealand",
  country: "New Zealand",
  contactEmailFallback: "hello@burtonbikes.co.nz",
  privacyEmailFallback: "privacy@burtonbikes.co.nz",
} as const

// ─── Body rendering ─────────────────────────────────────────────────────────

export type LegalBlock =
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] }

/**
 * Parse a section's plain-text body into render blocks. Blank lines separate
 * blocks; a run of lines each starting with "- " becomes a bullet list.
 */
export function parseLegalBody(body: string): LegalBlock[] {
  const blocks: LegalBlock[] = []
  const chunks = body.replace(/\r\n/g, "\n").split(/\n\s*\n/)

  for (const chunk of chunks) {
    const lines = chunk.split("\n").map((line) => line.trim()).filter(Boolean)
    if (lines.length === 0) continue

    const bulletLines = lines.filter((line) => line.startsWith("- "))
    if (bulletLines.length === lines.length) {
      blocks.push({ kind: "list", items: lines.map((line) => line.slice(2).trim()) })
    } else {
      blocks.push({ kind: "paragraph", text: lines.join(" ") })
    }
  }

  return blocks
}

// ─── Default content ────────────────────────────────────────────────────────

export const DEFAULT_LEGAL_DOCS: LegalDoc[] = [
  // 1. Terms & Conditions
  {
    slug: "terms",
    eyebrow: "Legal",
    title: "Terms & Conditions",
    intro:
      "These terms explain how ordering a bike or frame from Burton Bikes works. Please read them before placing an order. Nothing in these terms limits your rights under New Zealand consumer law.",
    sections: [
      {
        heading: "Who we are",
        body:
          "Burton Bikes is a custom bicycle maker based in Cambridge, New Zealand. We design and build bikes and frames to order for each individual rider. When we say \"we\", \"us\" or \"Burton Bikes\" in these terms, we mean the Burton Bikes business. When we say \"you\", we mean the customer placing an order.",
      },
      {
        heading: "How ordering works",
        body:
          "Most orders begin as an enquiry through our builder or contact form. From there we talk through your riding, geometry, sizing, and specification before anything is confirmed. An order is only placed once we have agreed the final build together and you have approved it.\n\nPrices shown on the website are a guide. Your final price is confirmed in writing before you pay a deposit, because custom work, options, and parts availability can change the total.",
      },
      {
        heading: "Custom build process",
        body:
          "Every Burton bike is custom made to order. Before manufacture begins you will be asked to approve your geometry, sizing, colour, finish, and specification. We do not start building until you have confirmed these details are correct. Please check them carefully — see our Custom Order Terms for more.",
      },
      {
        heading: "Deposits and payment stages",
        body:
          "Custom orders are usually paid in stages. A deposit is taken to confirm your order and begin production, with the balance due before delivery. The exact amounts and timing are set out in your written order confirmation.\n\nYour deposit reserves your build slot and covers work and materials committed to your order. Once manufacture of your custom build has started, a change-of-mind refund of the deposit may not be available because the work is specific to you.",
      },
      {
        heading: "Changes and cancellations by you",
        body:
          "If you would like to change your build, tell us as early as possible. Before manufacture starts we can usually adjust the specification, though changes may affect the price and lead time. Once your build has been approved and manufacturing has started, some changes may no longer be possible.\n\nChange-of-mind refunds may not be available once a custom order has started. This does not limit your rights under New Zealand consumer law if something is faulty.",
      },
      {
        heading: "Cancellations or refusals by Burton Bikes",
        body:
          "In rare cases we may need to decline or cancel an order — for example if a build cannot be made safely, if parts become unavailable, or if details cannot be agreed. If this happens before work has started, we will refund any deposit you have paid. If work has already started, we will discuss a fair outcome with you.",
      },
      {
        heading: "Lead times and delays",
        body:
          "Lead times are estimates, not guarantees. Custom manufacturing, parts supply, and shipping can all affect timing. If your build is going to be delayed, we will let you know and keep you updated. See Shipping & Delivery for more detail.",
      },
      {
        heading: "Shipping and delivery",
        body:
          "We can arrange delivery or, where practical, hand your bike over in person. Shipping details, costs, and responsibilities are covered in our Shipping & Delivery page.",
      },
      {
        heading: "Returns, refunds, and warranty",
        body:
          "Faulty products are handled under New Zealand consumer law. Full detail on faults, warranty, and what is and is not covered is set out in our Refunds, Returns & Warranty page.",
      },
      {
        heading: "Limits of liability",
        body:
          "To the extent allowed by law, Burton Bikes is not responsible for indirect or unexpected losses beyond the reasonable value of the product or service supplied. Nothing in these terms excludes or limits any rights or remedies you have under New Zealand consumer law, including the Consumer Guarantees Act 1993 and the Fair Trading Act 1986.",
      },
      {
        heading: "Your responsibility to check order details",
        body:
          "Because your bike is made specifically for you, it is important that you check your order details before approving them. This includes your geometry, sizing, colour, finish, specification, and delivery address. Please tell us straight away if anything looks wrong.",
      },
    ],
  },

  // 2. Privacy Policy
  {
    slug: "privacy",
    eyebrow: "Legal",
    title: "Privacy Policy",
    intro:
      "This policy explains what personal information Burton Bikes collects, why we collect it, who we may share it with, and how you can access or correct it. We handle your information in line with the New Zealand Privacy Act 2020.",
    sections: [
      {
        heading: "What information we collect",
        body:
          "Depending on how you interact with us, we may collect:\n\n- Your name\n- Your email address\n- Your phone number\n- Your delivery or billing address\n- Order and build details, including your custom geometry and specification\n- Newsletter and marketing sign-up preferences\n- Website analytics data, such as pages visited and general device information",
      },
      {
        heading: "Why we collect it",
        body:
          "We collect this information so we can:\n\n- Respond to your enquiries\n- Design, build, and deliver your custom bike or frame\n- Take payment and keep accurate order records\n- Provide support, warranty, and after-sales help\n- Send you updates or marketing if you have asked to receive them\n- Understand and improve how our website is used",
      },
      {
        heading: "Who we may share it with",
        body:
          "We do not sell your personal information. We only share it where needed to run our business and deliver your order. This may include:\n\n- Payment providers, to process payments securely\n- Shipping and courier providers, to deliver your order\n- Content management and website tools that host our site\n- Email and newsletter tools, to send messages you have opted into\n- Analytics tools, to understand website usage\n\nThese providers only receive the information they need for their task, and are expected to protect it.",
      },
      {
        heading: "Accessing or correcting your information",
        body:
          "You can ask us what personal information we hold about you, and ask us to correct it if it is wrong. Contact us using the privacy email below and we will help. There is normally no charge to make a request.",
      },
      {
        heading: "How long we keep it",
        body:
          "We keep your information only as long as we need it for the purposes above, or as required for legal, tax, and accounting reasons. When it is no longer needed, we securely delete or de-identify it.",
      },
      {
        heading: "If there is a privacy breach",
        body:
          "We take reasonable steps to keep your information secure. If a privacy breach happens that is likely to cause you serious harm, we will notify you and the Office of the Privacy Commissioner as required under the Privacy Act 2020.",
      },
      {
        heading: "Contact us about privacy",
        body:
          "If you have any questions about your privacy or this policy, please get in touch using the privacy contact email shown on this page.",
      },
    ],
  },

  // 3. Refunds, Returns & Warranty
  {
    slug: "refunds-returns-warranty",
    eyebrow: "Trust",
    title: "Refunds, Returns & Warranty",
    intro:
      "We want you to be happy with your Burton bike. This page explains how we handle faults, returns, and warranty. Your rights under New Zealand consumer law always apply.",
    sections: [
      {
        heading: "Faulty products",
        body:
          "Faulty products are handled under New Zealand consumer law, including the Consumer Guarantees Act 1993. If something is faulty, please contact us first so we can look into it.",
      },
      {
        heading: "Manufacturing defects",
        body:
          "If your bike or frame has a genuine manufacturing defect, we will put it right. Depending on the issue, that may mean a repair, a replacement, or a refund. We will work with you to find a fair outcome.",
      },
      {
        heading: "Change-of-mind on custom orders",
        body:
          "Because each bike is made specifically for you, change-of-mind refunds may not be available once a custom order has started. If you are unsure about any part of your build, please raise it with us before you approve it for manufacture.",
      },
      {
        heading: "What is not covered as a manufacturing defect",
        body:
          "Crash damage, misuse, incorrect setup, normal wear, and unauthorised modification are not covered as manufacturing defects. This includes:\n\n- Damage caused by a crash or impact\n- Normal wear from riding, such as worn parts, bearings, or finish\n- Incorrect assembly, setup, or torque\n- Using parts that are not compatible with the frame\n- Modifications or repairs not approved by Burton Bikes\n\nThis does not limit your rights under New Zealand consumer law if a product is genuinely faulty.",
      },
      {
        heading: "Contact us first",
        body:
          "Please contact us before returning anything. We will talk through the issue, work out the best next step, and give you clear instructions. Sending an item back without contacting us first can slow things down.",
      },
    ],
  },

  // 4. Custom Order Terms
  {
    slug: "custom-order-terms",
    eyebrow: "Legal",
    title: "Custom Order Terms",
    intro:
      "Every Burton bike and frame is custom made to order. These terms explain what that means for approvals, changes, pricing, and timing.",
    sections: [
      {
        heading: "Made to order",
        body:
          "Each bike and frame is custom made to order for a single rider. Your build is not held in stock — it is created specifically for you based on the details you approve.",
      },
      {
        heading: "Approval before manufacture",
        body:
          "Final geometry, sizing, finish, and specification must be approved before manufacture. We will confirm these details with you, and manufacture only begins once you have approved them.",
      },
      {
        heading: "Changes once work has started",
        body:
          "Once your order is approved and manufacturing has started, changes may not be possible. If a change can be made, it may affect your price and lead time.",
      },
      {
        heading: "Extra customisation",
        body:
          "Additional customisation beyond the standard build may increase the price and extend the lead time. We will always confirm any change in cost or timing with you before proceeding.",
      },
      {
        heading: "Approval confirmation",
        body:
          "As part of placing a custom order, you will be asked to confirm the following before we begin:\n\n- “I confirm the geometry, sizing, finish, and specification shown above are correct and approved for manufacture.”",
      },
    ],
  },

  // 5. Shipping & Delivery
  {
    slug: "shipping-delivery",
    eyebrow: "Trust",
    title: "Shipping & Delivery",
    intro:
      "This page explains where we ship, how long custom builds take, how shipping is costed, and what happens if something goes wrong in transit.",
    sections: [
      {
        heading: "Where we ship",
        body:
          "We are based in Cambridge, New Zealand and deliver throughout New Zealand. Where practical, we can also arrange international shipping — please ask us for details before ordering.",
      },
      {
        heading: "Estimated lead times",
        body:
          "Because each bike is built to order, lead times vary with the build. We will give you an estimated timeframe when we confirm your order. Lead times are estimates, not guarantees.",
      },
      {
        heading: "Custom build delays",
        body:
          "Custom manufacturing and parts supply can sometimes cause delays. If your build is going to take longer than expected, we will let you know and keep you updated on progress.",
      },
      {
        heading: "Shipping costs",
        body:
          "Shipping is either quoted or calculated based on your location and the size of your order. We will confirm any shipping cost with you before delivery.",
      },
      {
        heading: "International duties and taxes",
        body:
          "For orders shipped outside New Zealand, any import duties, taxes, or customs charges are usually the responsibility of the customer. Please check your local requirements before ordering internationally.",
      },
      {
        heading: "Lost or damaged shipments",
        body:
          "If your order arrives damaged, or does not arrive, please contact us as soon as possible. We will work with the courier and with you to resolve it. Keeping the packaging and any photos helps us sort it out quickly.",
      },
      {
        heading: "Correct delivery address",
        body:
          "Please make sure the delivery address you give us is correct. Burton Bikes is not responsible for delays or lost deliveries caused by an incorrect or incomplete address.",
      },
    ],
  },

  // 6. Safety & Intended Use
  {
    slug: "safety-intended-use",
    eyebrow: "Trust",
    title: "Safety & Intended Use",
    intro:
      "Your safety matters to us. This page explains how your bike should be assembled and maintained, what it is designed for, and the checks that keep it safe to ride.",
    sections: [
      {
        heading: "Assembly and checks",
        body:
          "Your bike or frame should be assembled and checked by a competent bike mechanic before it is ridden. Correct assembly and setup are essential for safe riding.",
      },
      {
        heading: "Intended use",
        body:
          "Each Burton platform is designed for a particular style of riding — such as enduro, downhill, or trail. We will confirm the intended use of your bike with you. Please use your bike within its intended use.",
      },
      {
        heading: "After a crash",
        body:
          "If you crash or have a heavy impact, inspect the frame and components carefully before riding again. If you are unsure whether something is damaged, have it checked by a competent mechanic.",
      },
      {
        heading: "Regular inspection and servicing",
        body:
          "Bolts, bearings, pivots, and bonded areas need regular checks. Follow the recommended torque specifications and service intervals to keep your bike safe and performing well.",
      },
      {
        heading: "Parts and modifications",
        body:
          "Use only compatible parts, and follow the correct fitting and torque guidance. Modifications may void your warranty. If you are planning a change, talk to us first.",
      },
      {
        heading: "Manuals and spec sheets",
        body:
          "Downloadable manuals and spec sheets for your bike will be made available. If you need guidance in the meantime, please get in touch.",
      },
    ],
  },

  // 7. Cookie / Tracking Notice
  {
    slug: "cookies",
    eyebrow: "Legal",
    title: "Cookie & Tracking Notice",
    intro:
      "This notice explains the cookies and tracking our website may use, and how you can manage them.",
    sections: [
      {
        heading: "Analytics",
        body:
          "We may use analytics tools to understand how our website is used — for example, which pages are visited and how the site performs. This helps us improve the site.",
      },
      {
        heading: "Performance tracking",
        body:
          "We may collect basic performance information, such as page load times and general device details, to keep the website running smoothly.",
      },
      {
        heading: "Marketing pixels",
        body:
          "If we use marketing pixels, they help us measure the reach of our marketing. We will only use these where appropriate.",
      },
      {
        heading: "Email marketing tracking",
        body:
          "If you have signed up to our emails, we may use standard email tracking to see whether messages were opened or links were clicked. You can unsubscribe from marketing emails at any time.",
      },
      {
        heading: "Managing or opting out",
        body:
          "Where practical, you can manage or opt out of tracking through your browser settings, your email unsubscribe link, or by contacting us. For more on how we handle your information, see our Privacy Policy.",
      },
    ],
  },
]

export function getLegalDoc(slug: string, docs: LegalDoc[] = DEFAULT_LEGAL_DOCS): LegalDoc | undefined {
  return docs.find((doc) => doc.slug === slug)
}

/** Footer navigation order for the legal / trust links (labels + slugs). */
export const LEGAL_FOOTER_LINKS: Array<{ slug: string; label: string }> =
  DEFAULT_LEGAL_DOCS.map((doc) => ({ slug: doc.slug, label: doc.title }))
